import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { setAccessToken } from '@/data/store'
import { AFinishreg } from '@/screens/a-finishreg/AFinishreg'
import { AOrgblocked } from '@/screens/a-orgblocked/AOrgblocked'
import type { components } from '@/api/generated/schema'

type OrganisationMembership = components['schemas']['OrganisationMembership']

/**
 * §3.1i: `a-finishreg` is entered from "any authenticated call" returning `401 no_identity`;
 * `a-orgblocked` from "any org-scoped call, at any time" returning `403 ORG_BLOCKED`. The FIRST
 * of each, right after authentication, is resolved here rather than by a global interceptor
 * (which needs an authenticated app already running to intercept from — this pass builds the
 * first authenticated screen, so it resolves its own first call). A later pass wiring a real
 * interceptor for MID-session arrivals does not change this component's job.
 *
 * `identityListOrganisations`: "**an empty list is a real state**: it is what an orphaned
 * identity sees, and the client routes it to the repair rather than rendering an empty
 * application" (`api/paths/identity/orgs.yaml`).
 */
export function OrgResolver({ onResolved }: { onResolved: (orgId: string, organisations: readonly OrganisationMembership[]) => void }) {
  const orgs = useQuery({
    queryKey: ['organisations'],
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs', {})
      if (error) throw new ApiProblemError(error as Problem)
      return data.items as unknown as readonly OrganisationMembership[]
    },
  })

  const organisations = orgs.data ?? []
  const reachable = organisations.find((o) => !o.access_blocked)

  useEffect(() => {
    if (reachable) onResolved(reachable.org_id, organisations)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachable?.org_id])

  if (orgs.isLoading) return null

  if (orgs.isError) {
    const problem = orgs.error instanceof ApiProblemError ? orgs.error.problem : undefined
    if (problem?.refusal_kind === 'NO_IDENTITY') {
      return <AFinishreg onFinished={() => orgs.refetch()} />
    }
    return null
  }

  if (organisations.length === 0) {
    return <AFinishreg onFinished={() => orgs.refetch()} />
  }

  if (!reachable) {
    return (
      <AOrgblocked
        orgName={organisations[0]!.name}
        onOrgSwitched={() => orgs.refetch()}
        onSignedOut={() => {
          setAccessToken(null)
          window.location.reload()
        }}
      />
    )
  }

  return null
}
